import { Component, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService, LocalStorageService } from 'src/app/core/services';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { toNumber } from 'ng-zorro-antd/core/util';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { PushNotificationDetailsComponent } from '../push-notification-details/push-notification-details.component';
import { RejectionReasonComponent } from 'src/app/shared/components/rejection-reason/rejection-reason.component';

@Component({
  selector: 'app-push-notifications',
  templateUrl: './push-notifications.component.html',
  styleUrls: ['./push-notifications.component.scss'],
})
export class PushNotificationsComponent implements OnInit {
  // ─── Table ───────────────────────────────────────────────────────
  allNotifications: any[] = [];
  allApprovedNotifications: any[] = [];
  totalDataCount = 0;
  totalApprovedDataCount = 0;
  pageSize = 10;
  pageIndex = 1;
  loading = false;
  index = 0;

  // ─── Filters ─────────────────────────────────────────────────────
  selectedType: string | null = null;
  selectedStatus: string | null = null;
  selectedModule: string | null = null;
  startDate: any = null;
  endDate: any = null;
  dateRange: any = null;

  // ─── Search ──────────────────────────────────────────────────────
  search = '';
  searchSubject: Subject<string> = new Subject<string>();
  userRole: string = '';
  imageBaseUrl = environment.mediaBaseUrl;
  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private modalService: NzModalService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private drawerService: NzDrawerService,
  ) {
    this.userRole = this.localStorageService.getItem('user').role;
    const snapIndex = toNumber(
      this.activatedRoute.snapshot.queryParams['index'],
    );
    if (snapIndex) {
      this.index = snapIndex;
    }
  }

  ngOnInit(): void {
    this.initializeSearchSubjects();
    this.getNotifications(this.index);
  }

  // ─── Tab / Index ─────────────────────────────────────────────────
  changeIndex(data: any): void {
    this.index = data;
    this.router.navigate([], { queryParams: { index: data } });
    this.pageIndex = 1;
    this.allNotifications = [];
    this.allApprovedNotifications = [];
    this.getNotifications(this.index);
  }

  initializeSearchSubjects(): void {
    this.searchSubject.pipe(debounceTime(800)).subscribe((value) => {
      this.search = value;
      this.pageIndex = 1;
      this.getNotifications();
    });
  }

  // ─── Fetch ───────────────────────────────────────────────────────
  getNotifications(index: number = this.index): void {
    const params: any = {
      skip: (this.pageIndex - 1) * this.pageSize,
      limit: this.pageSize,
    };

    if (this.search) {
      params.search = this.search;
    } else {
      delete params.search;
    }
    if (this.selectedType) {
      params.type = this.selectedType;
    } else {
      delete params.type;
    }
    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    } else {
      delete params.status;
    }
    if (this.selectedModule) {
      params.targetModule = this.selectedModule;
    } else {
      delete params.targetModule;
    }

    if (this.startDate && this.endDate) {
      params.startDate = this.startDate;
      params.endDate = this.endDate;
    } else {
      delete params.startDate;
      delete params.endDate;
    }

    /* Approval status filter by tab + role */
    if (index === 0) {
      params.approveStatus = 'approved';
    } else if (index === 1 && this.userRole === 'admin') {
      params.approveStatus = 'pending';
    } else if (index === 1 && this.userRole === 'subAdmin') {
      params.approveStatus = ['pending', 'rejected'];
    }

    this.loading = true;
    this.httpRequestService
      .request('get', 'push-notifications', params)
      .subscribe(
        (result: any) => {
          this.loading = false;
          if (index === 0) {
            this.allApprovedNotifications = result.data || [];
            this.totalApprovedDataCount = result.totalCount || 0;
          } else {
            this.allNotifications = result.data || [];
            this.totalDataCount = result.totalCount || 0;
          }
        },
        (err: any) => {
          this.loading = false;
          this.notificationService.error(
            'Error',
            'Failed to fetch notifications',
          );
        },
      );
  }

  // ─── Pagination ──────────────────────────────────────────────────
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex } = params;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.getNotifications(this.index);
  }

  // ─── Filter Handlers ─────────────────────────────────────────────
  private resetAndFetch(): void {
    this.pageIndex = 1;
    this.allNotifications = [];
    this.allApprovedNotifications = [];
    this.getNotifications(this.index);
  }

  onFilterChange(): void {
    this.resetAndFetch();
  }

  onChangeDateRange(date: any): void {
    if (date && date.length === 2) {
      this.startDate = date[0];
      this.endDate = date[1];
    } else {
      this.startDate = null;
      this.endDate = null;
    }
    this.resetAndFetch();
  }

  searchNotifications(event: string): void {
    this.searchSubject.next(event);
  }

  // ─── Navigate ────────────────────────────────────────────────────
  navigateToAdd(): void {
    this.router.navigate(['/main/push-notifications/add'], {
      queryParams: { index: this.index },
    });
  }

  navigateToEdit(id: string): void {
    this.router.navigate(['/main/push-notifications/update', id], {
      queryParams: { index: this.index },
    });
  }

  openDetails(data: any): void {
    this.drawerService.create({
      nzTitle: 'Push Notification Details',
      nzWidth: '50%',
      nzContent: PushNotificationDetailsComponent,
      nzContentParams: {
        value: data
      }
    });
  }

  // ─── Delete ──────────────────────────────────────────────────────
  confirmDelete(id: string): void {
    this.modalService.confirm({
      nzTitle: 'Delete Notification',
      nzContent:
        'Are you sure you want to delete this notification? This action cannot be undone.',
      nzOkText: 'Delete',
      nzOkDanger: true,
      nzOnOk: () => this.deleteNotification(id),
      nzCancelText: 'Cancel',
    });
  }

  deleteNotification(id: string): void {
    this.httpRequestService
      .request('delete', `push-notifications/${id}`)
      .subscribe(
        () => {
          this.notificationService.success(
            'Success',
            'Notification deleted successfully',
          );
          if (this.allNotifications.length === 1 && this.pageIndex > 1) {
            this.pageIndex--;
          }
          this.getNotifications(this.index);
        },
        () => {
          this.notificationService.error(
            'Error',
            'Failed to delete notification',
          );
        },
      );
  }

  // ─── Helpers ─────────────────────────────────────────────────────
  getTypeClass(type: string): string {
    const map: any = { general: 'tag-blue', user: 'tag-purple' };
    return map[type] || '';
  }

  getStatusClass(status: boolean): string {
    return status ? 'tag-green' : 'tag-red';
  }

  getModuleClass(module: string): string {
    const map: any = {
      currentAffair: 'tag-orange',
      editorial: 'tag-cyan',
      testSery: 'tag-geekblue',
      plan: 'tag-gold',
    };
    return map[module] || '';
  }

  getModuleLabel(module: string): string {
    const map: any = {
      currentAffair: 'Current Affair',
      editorial: 'Editorial',
      testSery: 'Test Series',
      plan: 'Plan',
    };
    return map[module] || module || 'N/A';
  }

  updateStatus(id: string, status: boolean): void {
    this.httpRequestService
      .request('put', `push-notifications/active-inactive/${id}`, {
        status: !status,
      })
      .subscribe(() => {
        this.notificationService.success('', 'Status Updated Successfully');
        this.getNotifications(this.index);
      });
  }

  updateApproveStatus(id: string, approveStatus: string): void {
    this.httpRequestService
      .request('put', `push-notifications/approved-rejected/${id}`, {
        approveStatus,
      })
      .subscribe(() => {
        this.notificationService.success('', 'Status Updated Successfully');
        this.getNotifications(this.index);
      });
  }

  openRejectionReason(id: string): void {
    const modal = this.modalService.create({
      nzTitle: 'Add A Reason',
      nzContent: RejectionReasonComponent,
      nzWidth: '800px',
      nzFooter: null,
      nzComponentParams: {
        updateId: id,
        component: 'push-notifications',
      },
      nzCancelText: null,
      nzOkText: null,
    });
    modal.afterClose.subscribe(() => this.getNotifications(this.index));
  }
}
